import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { App as AntApp, Button, Input, Modal, Select, Space, Spin } from "antd";
import { ArrowLeftOutlined, CalendarOutlined, UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined } from "@ant-design/icons";
import styles from "./OrderDetailPage.module.css";
import PageContainer from "../../components/PageContainer/PageContainer";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useAuth } from "../../context/AuthContext";
import { fetchOrderItems, updateOrderNote, bulkUpdateOrderStatus } from "./OrderUtils";
import {
  StatusTag,
  OrderTimeline,
  resolveOrderStatus,
  normStatus,
  orderSelectableForBulk,
  getBulkAllowedTransitions,
  getConditionalActionSplit,
  getActionLabel,
  getQuickActionButtonProps,
  getBulkActionConfirmModal,
  extractOrderLineItems,
} from "./OrderDashboard";
import OrderNoteChat from "./OrderNoteChat";
import { resolveDeliveryDetailsFromShippingAddress } from "../../components/common/DeliveryDetails";
import SelectionSummaryDisplay from "../../components/SelectionSummaryDisplay";
import CustomisationValuesDisplay from "../../components/CustomisationValuesDisplay";
import { getCurrencySymbol } from "../../utils/currency";
import { cdnUrl } from "../../config/mediaPaths";
import { ORDER_DASHBOARD_LABELS } from "../../labels/orderDashboardLabels";

const formatDateTime = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DeliveryDetailsWithIcons = ({ shippingAddress }) => {
  const delivery = resolveDeliveryDetailsFromShippingAddress(shippingAddress);
  if (!delivery) {
    return <span style={{ color: "#999" }}>No delivery details on this order</span>;
  }
  const rows = [
    { key: "name", label: "Name", value: delivery.displayName || "—", icon: <UserOutlined /> },
    { key: "contact", label: "Contact", value: delivery.contact || "—", icon: <PhoneOutlined /> },
    { key: "email", label: "Email", value: delivery.email || "—", icon: <MailOutlined /> },
    { key: "address", label: "Address", value: delivery.fullAddress || "—", icon: <EnvironmentOutlined /> },
  ];
  return (
    <div className={styles.deliveryGrid}>
      {rows.map((row) => (
        <div key={row.key} className={styles.deliveryItem}>
          <span className={styles.deliveryIcon}>{row.icon}</span>
          <div className={styles.deliveryText}>
            <span className={styles.deliveryLabel}>{row.label}</span>
            <span className={styles.deliveryValue}>{row.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const OrderDetailPage = () => {
  const { message } = AntApp.useApp();
  const { token } = useAuth();
  const location = useLocation();

  const [viewOrder, setViewOrder] = useState(location.state?.order || null);
  const [orderItems, setOrderItems] = useState(null);
  const [orderItemsLoading, setOrderItemsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [singleModalTargetStatus, setSingleModalTargetStatus] = useState(null);
  const [singleOrderCancelModalOpen, setSingleOrderCancelModalOpen] = useState(false);
  const [singleOrderCancelReason, setSingleOrderCancelReason] = useState("");
  const [singleOrderCancelReasonError, setSingleOrderCancelReasonError] = useState("");
  const [singleOrderActionLoading, setSingleOrderActionLoading] = useState(false);

  const [modal, contextHolder] = Modal.useModal();

  const hubId = viewOrder?.rawData?.HubId;
  const orderId = viewOrder?.rawData?.OrderId;
  const bizId = viewOrder?.rawData?.BizId ?? viewOrder?.bizId;

  const refreshOrderItems = useCallback(async () => {
    if (!token || !hubId || !orderId) return;
    const response = await fetchOrderItems({ token, HubId: hubId, OrderId: orderId });
    setOrderItems(extractOrderLineItems(response));
  }, [token, hubId, orderId]);

  useEffect(() => {
    if (!token || !hubId || !orderId) return;
    let cancelled = false;
    setOrderItemsLoading(true);
    setLoadError(null);
    fetchOrderItems({ token, HubId: hubId, OrderId: orderId })
      .then((response) => {
        if (cancelled) return;
        setOrderItems(extractOrderLineItems(response));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || err?.message || "Failed to load order items.");
        setOrderItems([]);
      })
      .finally(() => {
        if (!cancelled) setOrderItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, hubId, orderId]);

  const allowedTransitions = useMemo(
    () => (viewOrder ? getBulkAllowedTransitions([viewOrder]) : []),
    [viewOrder]
  );
  const { quickActions, moreActions } = useMemo(
    () => getConditionalActionSplit(allowedTransitions),
    [allowedTransitions]
  );

  const syncStatusLocally = useCallback(
    (targetStatus, lastUpdatedOn) => {
      if (!viewOrder?.id || !targetStatus) return;
      const updatedOn = lastUpdatedOn || new Date().toISOString();
      setViewOrder((prev) => ({
        ...prev,
        status: normStatus(targetStatus),
        rawData: {
          ...(prev.rawData || {}),
          OrderStatus: targetStatus,
          LastUpdatedOn: updatedOn,
        },
      }));
    },
    [viewOrder?.id]
  );

  const applyStatus = useCallback(
    async (targetStatus, reasonText = "") => {
      if (!token || !targetStatus || !viewOrder) {
        message.warning("Nothing to update.");
        return;
      }
      if (!orderSelectableForBulk(viewOrder)) {
        message.warning("This order cannot be updated.");
        return;
      }
      if (!allowedTransitions.includes(targetStatus)) {
        message.warning("This action is not allowed for the current order status.");
        return;
      }
      setSingleOrderActionLoading(true);
      try {
        const statusResponse = await bulkUpdateOrderStatus({
          token,
          orders: [
            {
              ...viewOrder.rawData,
              NewStatus: targetStatus,
            },
          ],
        });
        const updatedEntry = (statusResponse?.data?.updated || []).find(
          (entry) => entry?.OrderId === viewOrder?.rawData?.OrderId
        );
        syncStatusLocally(targetStatus, updatedEntry?.LastUpdatedOn);
        if (targetStatus === "Cancelled") {
          if (!hubId || !orderId || !bizId) {
            message.warning("Order cancelled, but note could not be added due to missing ids.");
          } else {
            try {
              await updateOrderNote({
                token,
                HubId: hubId,
                BizId: bizId,
                OrderId: orderId,
                notes: `Reason For Cancellation: ${reasonText}`,
              });
              message.success("Order cancelled.");
            } catch {
              message.warning("Order cancelled, but failed to add cancellation note.");
            }
          }
        } else {
          message.success(`Order updated to ${targetStatus}.`);
        }
        await refreshOrderItems();
      } catch (err) {
        message.error(err?.response?.data?.message || err?.message || "Failed to update order.");
      } finally {
        setSingleOrderActionLoading(false);
      }
    },
    [token, viewOrder, allowedTransitions, hubId, orderId, bizId, syncStatusLocally, refreshOrderItems, message]
  );

  const handleActionApply = useCallback(
    (targetStatus) => {
      if (!targetStatus || !viewOrder) return;
      if (targetStatus === "Cancelled") {
        setSingleOrderCancelModalOpen(true);
        return;
      }
      const { title, content, okText, okButtonProps, cancelText } = getBulkActionConfirmModal(
        targetStatus,
        1
      );
      modal.confirm({
        title,
        content,
        okText,
        okButtonProps: okButtonProps ?? {},
        cancelText,
        onOk: () => applyStatus(targetStatus),
      });
    },
    [viewOrder, modal, applyStatus]
  );

  const handleCancelWithReason = useCallback(async () => {
    const reason = singleOrderCancelReason.trim();
    if (!reason) {
      setSingleOrderCancelReasonError("Reason is required.");
      return;
    }
    setSingleOrderCancelReasonError("");
    await applyStatus("Cancelled", reason);
    setSingleOrderCancelModalOpen(false);
    setSingleOrderCancelReason("");
  }, [singleOrderCancelReason, applyStatus]);

  useEffect(() => {
    setSingleModalTargetStatus(null);
    setSingleOrderCancelModalOpen(false);
    setSingleOrderCancelReason("");
    setSingleOrderCancelReasonError("");
  }, [viewOrder?.id]);

  if (!viewOrder) {
    return (
      <PageContainer>
        <div className={styles.page}>
          <div className={styles.container}>
            <Link to="/dashboard" className={styles.backLink}>
              <ArrowLeftOutlined /> Back to orders
            </Link>
            <div className={styles.errorCard}>No order selected. Please go back and open an order.</div>
          </div>
        </div>
      </PageContainer>
    );
  }

  const currencySymbol = getCurrencySymbol(viewOrder?.currency || "INR");
  const createdOn = viewOrder?.rawData?.CreatedOn || viewOrder?.rawData?.CreatedAt || "";

  return (
    <PageContainer>
      <div className={styles.page}>
        <div className={styles.container}>
          <Breadcrumb
            items={[
              { label: "Orders", path: "/dashboard" },
              { label: `Order ${viewOrder?.orderNumber ?? ""}` },
            ]}
          />
          {contextHolder}

          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Link to="/dashboard" className={styles.backLink}>
                <ArrowLeftOutlined /> Back to orders
              </Link>
              <span className={styles.headerDivider} />
              <h1 className={styles.headerTitle}>Order #{viewOrder?.orderNumber}</h1>
            </div>
            <div className={styles.headerRight}>
              {createdOn ? (
                <span className={styles.datePill}>
                  <CalendarOutlined /> {formatDateTime(createdOn)}
                </span>
              ) : null}
              <StatusTag status={resolveOrderStatus(viewOrder)} />
            </div>
          </div>

          <div className={styles.layout}>
            <div className={styles.leftContent}>
              <div className={styles.card}>
                <div className={styles.cardTitleRow}>
                  <h2 className={styles.cardTitle}>Order items</h2>
                  {allowedTransitions.length > 0 ? (
                    <Space wrap size="small" style={{ justifyContent: "flex-start" }}>
                      {quickActions.map((status) => (
                        <Button
                          key={status}
                          onClick={() => handleActionApply(status)}
                          loading={singleOrderActionLoading}
                          {...getQuickActionButtonProps(status)}
                        >
                          {getActionLabel(status)}
                        </Button>
                      ))}
                      {moreActions.length > 0 ? (
                        <>
                          <Select
                            placeholder="More"
                            style={{ minWidth: 180 }}
                            value={singleModalTargetStatus}
                            onChange={setSingleModalTargetStatus}
                            options={moreActions.map((s) => ({ value: s, label: getActionLabel(s) }))}
                            disabled={singleOrderActionLoading}
                          />
                          <Button
                            type="primary"
                            disabled={!singleModalTargetStatus}
                            loading={singleOrderActionLoading}
                            onClick={() => handleActionApply(singleModalTargetStatus)}
                          >
                            Apply
                          </Button>
                        </>
                      ) : null}
                    </Space>
                  ) : null}
                </div>

                {orderItemsLoading ? (
                  <div className={styles.loadingWrap}>
                    <Spin size="small" />
                  </div>
                ) : loadError ? (
                  <div className={styles.loadError}>{loadError}</div>
                ) : orderItems && orderItems.length > 0 ? (
                  <div className={styles.itemList}>
                    {orderItems.map((item, lineIndex) => {
                      const titleLabel =
                        item.ItemName && item.ItemName !== "Private line item"
                          ? item.ItemName
                          : `Line ${lineIndex + 1}`;
                      const qtyText = item.Quantity ?? item.quantity ?? 0;
                      const unitPrice = item.Price ?? item.price ?? 0;
                      const selectionText = item.SelectionSummary ?? item.selectionSummary ?? "";
                      const trimmedSummary = String(selectionText).trim();
                      const customisationValuesList = item.CustomisationValues || item.customisationValues || [];
                      const fileCustomisationLabelKeys = customisationValuesList
                        .filter((entry) => entry?.selected && entry?.type === "file" && entry?.name)
                        .map((entry) => String(entry.name).trim().toLowerCase());
                      const bookingDate = item.PrebookingSlotDate ?? item.prebookingSlotDate ?? "";
                      const bookingSlot =
                        item.NoSlots === true
                          ? ORDER_DASHBOARD_LABELS.dateOnlyBooking
                          : (item.PrebookingSlot ?? item.prebookingSlot ?? "");
                      const itemImageKey =
                        Array.isArray(item.ItemImages) && typeof item.ItemImages[0] === "string"
                          ? item.ItemImages[0]
                          : "";
                      const itemImageUrl =
                        itemImageKey && hubId && bizId
                          ? cdnUrl({ entity: "item", fileType: "media", hubId, bizId, fileName: itemImageKey })
                          : null;
                      return (
                        <div key={lineIndex} className={styles.itemRow}>
                          {itemImageUrl ? (
                            <img src={itemImageUrl} alt={titleLabel} className={styles.itemThumb} />
                          ) : (
                            <div className={styles.itemThumbPlaceholder} />
                          )}
                          <div className={styles.itemInfo}>
                            <div className={styles.itemTitle}>{titleLabel}</div>
                            {trimmedSummary ? (
                              <div className={styles.itemSummary}>
                                <SelectionSummaryDisplay
                                  summary={trimmedSummary}
                                  hiddenLabels={fileCustomisationLabelKeys}
                                />
                              </div>
                            ) : null}
                            {customisationValuesList.length > 0 ? (
                              <div className={styles.itemSummary}>
                                <CustomisationValuesDisplay
                                  values={customisationValuesList}
                                  selectionSummary={trimmedSummary}
                                  filesDeleted={
                                    ["completed", "cancelled"].includes(
                                      normStatus(viewOrder?.rawData?.OrderStatus)
                                    ) && viewOrder?.rawData?.autoDeleteUploadedFiles === true
                                  }
                                  orderId={viewOrder?.rawData?.OrderId}
                                  hubId={viewOrder?.rawData?.HubId}
                                  bizId={viewOrder?.rawData?.BizId}
                                />
                              </div>
                            ) : null}
                            {bookingDate ? (
                              <div className={styles.itemSummary}>
                                {ORDER_DASHBOARD_LABELS.bookingDateLabel}: {bookingDate}
                                {bookingSlot
                                  ? ` | ${ORDER_DASHBOARD_LABELS.bookingSlotLabel}: ${bookingSlot}`
                                  : ""}
                              </div>
                            ) : null}
                            <div className={styles.itemQty}>
                              {qtyText} × {currencySymbol}
                              {Number(unitPrice).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className={styles.noItems}>No items</span>
                )}
              </div>

              <div className={styles.card}>
                <h2 className={styles.cardTitle}>Delivery details</h2>
                <DeliveryDetailsWithIcons
                  shippingAddress={viewOrder?.rawData?.ShippingAddress ?? viewOrder?.rawData?.shippingAddress}
                />
              </div>

              <div className={styles.card}>
                <h2 className={styles.cardTitle}>Order timeline</h2>
                <OrderTimeline
                  status={resolveOrderStatus(viewOrder)}
                  orderKey={viewOrder.id}
                  createdOn={createdOn}
                  lastUpdatedOn={viewOrder?.rawData?.LastUpdatedOn}
                />
              </div>
            </div>

            <div className={styles.rightSidebar}>
              <div className={styles.chatCard}>
                <div className={styles.chatHeader}>
                  <div className={styles.chatAvatar}>{(bizId || "?")[0].toUpperCase()}</div>
                  <div className={styles.chatInfo}>
                    <p className={styles.chatTitle}>Order notes</p>
                    <p className={styles.chatSubtitle}>Reply to the customer conversation.</p>
                  </div>
                </div>
                <div className={styles.chatBody}>
                  <OrderNoteChat order={viewOrder} onRefreshOrders={refreshOrderItems} fillBody />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal
          title="Cancel this order?"
          open={singleOrderCancelModalOpen}
          okText="Cancel order"
          okButtonProps={{ danger: true }}
          confirmLoading={singleOrderActionLoading}
          onOk={handleCancelWithReason}
          onCancel={() => {
            if (singleOrderActionLoading) return;
            setSingleOrderCancelModalOpen(false);
            setSingleOrderCancelReason("");
            setSingleOrderCancelReasonError("");
          }}
        >
          <div style={{ marginBottom: 12 }}>
            This will cancel this order. This may not be reversible.
          </div>
          <Input.TextArea
            value={singleOrderCancelReason}
            onChange={(e) => {
              setSingleOrderCancelReason(e.target.value);
              if (singleOrderCancelReasonError && e.target.value.trim()) {
                setSingleOrderCancelReasonError("");
              }
            }}
            placeholder="Enter cancellation reason"
            autoSize={{ minRows: 3, maxRows: 5 }}
            status={singleOrderCancelReasonError ? "error" : ""}
          />
          {singleOrderCancelReasonError ? (
            <div style={{ color: "var(--zh-danger)", fontSize: 12, marginTop: 6 }}>
              {singleOrderCancelReasonError}
            </div>
          ) : null}
        </Modal>
      </div>
    </PageContainer>
  );
};

export default OrderDetailPage;
